/**
 * `Select` options from an `as const` enum object and a labels map keyed by its values —
 * every member, in declaration order. For a labels map with no enum behind it, or for a
 * subset, use `labelsToOptions` (string values, for filters) or `idsToOptions` (numeric,
 * for forms).
 *
 * **Not a numeric TypeScript `enum`**: it compiles to an object with a reverse mapping, and
 * every member comes out twice — once more as its own name, with no label.
 */
export function enumToOptions<
  T extends Record<string, string | number>,
  L extends Record<T[keyof T], string>
>(enumObj: T, labels: L) {
  return Object.values(enumObj).map((value) => ({
    value,
    label: labels[value as T[keyof T]]
  }))
}

export interface SelectOption {
  label: string
  value: string
}

/**
 * `Select` options straight from a labels map, with **string** values — the shape a filter
 * needs. A filter value rides the URL and comes back as a string, so options built with
 * numeric ids leave the select unable to match its own value. `idsToOptions` is the numeric
 * sibling, for a form field.
 *
 * Pass `only` to offer a subset, in the order it is given.
 *
 * The value type is `` `${K}` `` and not a plain `string` so that a labels map keyed by a
 * string enum keeps that enum through here — whatever renders the options then emits the
 * enum and its handler needs no cast. **Do not annotate the result `SelectOption[]`** at a
 * call site: that erases it again.
 */
export function labelsToOptions<K extends string | number>(
  labels: Record<K, string>,
  only?: readonly K[]
): Array<{ label: string; value: `${K}` }> {
  const keys = only ?? (Object.keys(labels) as unknown as K[])

  return keys.map((key) => ({
    value: String(key) as `${K}`,
    label: labels[key]
  }))
}

/**
 * The two options of a filter over a boolean column, valued `'true'` / `'false'` as strings
 * because a filter value rides the URL.
 *
 * For a **form** field the values have to stay real booleans — that is `flagOptions`.
 */
export function booleanOptions(
  trueLabel: string,
  falseLabel: string
): SelectOption[] {
  return [
    { label: trueLabel, value: 'true' },
    { label: falseLabel, value: 'false' }
  ]
}

interface BooleanSelectOption {
  label: string
  value: boolean
}

/**
 * The form-side sibling of `booleanOptions`: the same two states with real boolean values,
 * for a radio group or select bound to a boolean form field, which is compared and
 * submitted as a boolean and so cannot match the string `'true'`.
 *
 * The labels are given **in the order they render**, off first — the opposite of
 * `booleanOptions`, so the two are not drop-in replacements for each other.
 */
export function flagOptions(
  falseLabel: string,
  trueLabel: string
): BooleanSelectOption[] {
  return [
    { label: falseLabel, value: false },
    { label: trueLabel, value: true }
  ]
}

export interface NumericSelectOption {
  label: string
  value: number
}

/**
 * The numeric counterpart of `labelsToOptions` — same arguments in the same order, values
 * kept as numbers, for a form field whose value never rides the URL. `only` offers a subset
 * in the order it is given; a label a map has no entry for falls back to the id itself.
 *
 * Generic over the id so a literal union of ids survives into the options. Annotating the
 * result `NumericSelectOption[]` at a call site erases it.
 */
export function idsToOptions<TId extends number>(
  labels: Record<TId, string>,
  only?: readonly TId[]
): Array<{ label: string; value: TId }> {
  const ids = only ?? (Object.keys(labels).map(Number) as TId[])

  return ids.map((value) => ({
    value,
    label: labels[value] ?? String(value)
  }))
}
